import { selector } from 'recoil';

import {
  CollectionSurveyQuestions,
  CollectionSurveyQuestionType
} from '@src/generated/hooks';
import { surveyAtom } from '@src/recoil/atoms/survey';
import {
  parseEqualsLogic,
  parseHasContent,
  parseHasNoContent,
  parseHasNotLogic
} from '@src/services/surveys/utils/skipLogic';

export interface SelectSurveyQuestions {
  active: CollectionSurveyQuestions[];
  hidden: CollectionSurveyQuestions[];
}

/**
 * @name selectSurveyQuestions
 * @external https://recoiljs.org/docs/basic-tutorial/selectors
 * @description This selector grabs our raw survey and filters the
 * questions into two buckets, active and hidden.
 */
export const selectSurveyQuestions = selector({
  get: ({ get }) => {
    const { answers, survey } = get(surveyAtom);

    // Setup
    const notRendered: CollectionSurveyQuestionType[] = [
      'uuid',
      'query_param',
      'prefill_single',
      'prefill_multiple'
    ];
    const questions = survey.questions ?? [];

    // Store the position of each question in the list
    const withId = questions.map((q, i) => ({ ...q, _id: i + 1 }));

    // Filter any hidden, skipped, or otherwise one-off questions
    const active = withId.filter((question) => {
      const { skip_rules: rules, type } = question;

      if (notRendered.includes(type)) return false;

      // We always want to render this question
      if (!rules) return true;

      const { question_id } = rules;
      const refQuestion = withId.find((q) => q.id === question_id);

      const ruleType = rules.rule_type;
      const answer = answers[refQuestion?.name ?? '__missing__'];
      const values = rules.values ? [...rules.values] : [];

      // Just cleaning up...
      switch (ruleType) {
        case 'contains':
          return !parseHasNotLogic(answer, values);
        case 'equals':
          return parseEqualsLogic(answer, values);
        case 'hasContent':
          return parseHasContent(answer);
        case 'hasNoContent':
          return parseHasNoContent(answer);
        case 'hasNot':
          return parseHasNotLogic(answer, values);
      }
    });

    // TODO: Handle these on initialization
    const hidden = withId.filter((question) =>
      notRendered.includes(question.type)
    );

    return {
      active,
      hidden
    };
  },

  key: 'selectSurveyQuestions'
});
